import React from "react";
import PropTypes from "prop-types";
import { IconButton, Typography, makeStyles } from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import CloseIcon from "@material-ui/icons/Close";
import A_Button from "../../../../Atoms/Inputs/Buttons/A_Button";

const STRINGS = {
  TITLE: "Existing Applications Found",
  buildMessage: (fieldLabel) =>
    `We found matching records for the ${fieldLabel} entered. Please review the results in the ${fieldLabel} field to avoid duplicates.`,
  VIEW_LIST: "View List",
  DISMISS: "Dismiss notification",
};

const useStyles = makeStyles((theme) => ({
  banner: {
    position: "relative",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 2),
    borderRadius: "calc(12px * var(--app-radius-scale, 1))",
    border: "1px solid #F59E0B",
    backgroundColor: "#FEF3C7",
    transition: "all 0.2s ease",
    [theme.breakpoints.down("xs")]: {
      flexWrap: "wrap",
      alignItems: "flex-start",
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 1.5),
      paddingRight: theme.spacing(5),
      "& $content": {
        flex: "1 1 auto",
        minWidth: 0,
      },
      "& $actions": {
        flex: "1 1 100%",
        justifyContent: "stretch",
        "& > *": {
          flex: 1,
        },
      },
    },
    'html[data-mode="dark"] &': {
      backgroundColor: "#451A03",
      border: "1px solid #B45309",
    },
    "& .MuiTypography-root": {
      color: "#92400E",
      'html[data-mode="dark"] &': {
        color: "#FBBF24",
      },
    },
    "& .MuiTypography-body2": {
      color: "#B45309",
      'html[data-mode="dark"] &': {
        color: "#FCD34D",
      },
    },
  },
  warningIcon: {
    color: "#D97706",
    'html[data-mode="dark"] &': {
      color: "#FBBF24",
    },
    fontSize: "1.25rem",
    flexShrink: 0,
  },
  content: {
    minWidth: 0,
    flex: 1,
  },
  title: {
    fontSize: "0.8125rem",
    fontWeight: 700,
    marginBottom: theme.spacing(0.25),
    color: "var(--color-text-primary)",
  },
  text: {
    fontSize: "0.75rem",
    color: "var(--color-text-secondary)",
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    [theme.breakpoints.down("xs")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: 1,
      },
    },
  },
  close: {
    color: "#92400E",
    padding: theme.spacing(0.5),
    flexShrink: 0,
    'html[data-mode="dark"] &': {
      color: "#FBBF24",
    },
    [theme.breakpoints.down("xs")]: {
      position: "absolute",
      top: theme.spacing(0.75),
      right: theme.spacing(0.75),
    },
  },
}));

const DuplicateLoanBanner = ({ fieldLabel, onView, onDismiss }) => {
  const classes = useStyles();

  return (
    <div className={classes.banner}>
      <WarningIcon className={classes.warningIcon} />
      <div className={classes.content}>
        <Typography className={classes.title}>{STRINGS.TITLE}</Typography>
        <Typography className={classes.text}>
          {STRINGS.buildMessage(fieldLabel)}
        </Typography>
      </div>
      <div className={classes.actions} onMouseDown={(e) => e.preventDefault()}>
        <A_Button
          label={STRINGS.VIEW_LIST}
          premiumVariant="secondary"
          onClick={onView}
        />
      </div>
      <IconButton
        size="small"
        aria-label={STRINGS.DISMISS}
        className={classes.close}
        onClick={onDismiss}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

DuplicateLoanBanner.propTypes = {
  fieldLabel: PropTypes.string.isRequired,
  onView: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

DuplicateLoanBanner.displayName = "DuplicateLoanBanner";

export default React.memo(DuplicateLoanBanner);
